import React, { useState, useMemo, useCallback } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Popover, OverlayTrigger } from "react-bootstrap";
import { ApiHook } from "../../hooks/apiHook";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import menuPlaceHolder from "../../examples/dashboardMenu.json";
import { isActionRestricted, getUserKycStatus } from "../../utils/kycRestrictions";
import LoginService from "../../services/auth/Login";

const LeftSection = ({
  isLeftMenuVisible,
  handleLeftMenuToggle,
  menus,
  spclMenu,
  toggleMenuRef,
  logo,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeSubMenuIndex, setActiveSubMenuIndex] = useState(-1);
  const [registerLinkCheck, setRegisterLinkCheck] = useState(false);
  const [storeLinkCheck, setStoreLinkCheck] = useState(false);
  const location = useLocation();
  
  // Get KYC restrictions from Redux
  const appLayout = useSelector((state) => state?.dashboard?.appLayout);
  const kycRestrictions = appLayout?.kycRestrictions || [];
  const userKycStatus = getUserKycStatus(appLayout);
  const shoppingRestriction = isActionRestricted(kycRestrictions, 'shopping', userKycStatus);
  
  // Handle shopping click with KYC check
  const handleShoppingClick = (e) => {
    if (shoppingRestriction.isRestricted) {
      e.preventDefault();
      toast.warning(shoppingRestriction.message || t("kycRestrictionMessage"));
      return false;
    }
    setStoreLinkCheck(true);
  };
  
  // State for events SSO loading
  const [eventsLoading, setEventsLoading] = useState(false);
  // State for LMS SSO loading
  const [lmsLoading, setLmsLoading] = useState(false);
  
  // Handle Events click - opens EMS in new tab via SSO
  const handleEventsClick = useCallback(async (e) => {
    e.preventDefault();
    if (eventsLoading) return;
    
    setEventsLoading(true);
    try {
      const result = await LoginService.getEmsLink();
      if (result?.redirectUrl) {
        // Open EMS in new tab with SSO code
        window.open(result.redirectUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error(t("events_link_error") || "Failed to get Events link");
      }
    } catch (error) {
      console.error("Events SSO error:", error);
      const apiMessage = error?.response?.data?.message
                      || error?.response?.data?.data?.message
                      || error?.message;
      toast.error(apiMessage || t("events_link_error") || "Failed to get Events link");
    } finally {
      setEventsLoading(false);
    }
  }, [eventsLoading, t]);

  // Handle LMS click - opens LMS in new tab via SSO (mirrors handleEventsClick)
  const handleLmsClick = useCallback(async (e) => {
    e.preventDefault();
    if (lmsLoading) return;

    setLmsLoading(true);
    try {
      const result = await LoginService.getLmsLink();
      if (result?.redirectUrl) {
        // Open LMS in new tab with SSO code
        window.open(result.redirectUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error(t("lms_link_error") || "Failed to get LMS link");
      }
    } catch (error) {
      console.error("LMS SSO error:", error);
      const apiMessage = error?.response?.data?.message
                      || error?.response?.data?.data?.message
                      || error?.message;
      toast.error(apiMessage || t("lms_link_error") || "Failed to get LMS link");
    } finally {
      setLmsLoading(false);
    }
  }, [lmsLoading, t]);
  
  // إضافة عنصر events و lms ديناميكياً إذا لم يكونا موجودين.
  // ويُحقن lms دائماً مباشرة بعد events ليكون أسفل EMS مباشرة في الواجهة.
  const menuItems = useMemo(() => {
    const baseMenus = menus && menus.length > 0 ? menus : menuPlaceHolder;
    if (!baseMenus || !Array.isArray(baseMenus)) {
      return menuPlaceHolder;
    }
    let result = baseMenus;
    // التحقق من وجود events في القائمة
    const hasEvents = result.some(item => item.slug === "events");
    if (!hasEvents) {
      const eventsMenuItem = {
        id: 30,
        title: "Events",
        slug: "events",
        userIcon: "event.svg",
        is_child: 0,
        MenuPermission: {
          userPermission: 1
        },
        subMenu: [],
        ecomLink: false,
        isMain: true
      };
      result = [...result, eventsMenuItem];
    }
    // إزالة أي عنصر lms قادم من الـ backend لتجنب التكرار، ثم حقنه مباشرة بعد events.
    result = result.filter(item => item.slug !== "lms");
    const lmsMenuItem = {
      id: 31,
      title: "LMS",
      slug: "lms",
      userIcon: "download-meterial.svg",
      is_child: 0,
      MenuPermission: {
        userPermission: 1
      },
      subMenu: [],
      ecomLink: false,
      isMain: true
    };
    const eventsIndex = result.findIndex(item => item.slug === "events");
    if (eventsIndex !== -1) {
      result = [
        ...result.slice(0, eventsIndex + 1),
        lmsMenuItem,
        ...result.slice(eventsIndex + 1),
      ];
    } else {
      result = [...result, lmsMenuItem];
    }
    // Inject RPS Status menu item
    const rpsStatusMenuItem = {
      id: 33,
      title: "Performance Status",
      slug: "rps-status",
      userIcon: "rank_class_ico.svg",
      is_child: 0,
      MenuPermission: { userPermission: 1 },
      subMenu: [],
      ecomLink: false,
      isMain: false
    };
    const toolsIndex = result.findIndex(item => item.slug === "tools");
    if (toolsIndex !== -1) {
      result = [
        ...result.slice(0, toolsIndex + 1),
        rpsStatusMenuItem,
        ...result.slice(toolsIndex + 1),
      ];
    } else {
      result = [...result, rpsStatusMenuItem];
    }
    return result;
  }, [menus]);

  const handleDropMenuClick = (index) => {
    if (activeSubMenuIndex === index) {
      setActiveSubMenuIndex(-1); // Close the submenu if it's already open
    } else {
      setActiveSubMenuIndex(index); // Open the clicked submenu
    }
  };
  const renderPopover = (
    content // popover the tilte in Menu
  ) => (
    <Popover>
      <Popover.Body>{content}</Popover.Body>
    </Popover>
  );
  const islinkActive = (link) => {
    if (link === "/networks") {
      return location.pathname === "/sponsor-tree" ||
        location.pathname === "/genealogy-tree" ||
        location.pathname === "/tree-view" ||
        location.pathname === "/downline-members" ||
        location.pathname === "/referral-members" ||
        location.pathname === "/network-analytics"
        ? "active"
        : "";
    } else if (location.pathname === link) {
      return "active";
    }
  };
  // ----------------------------- Api Call for Ecom Link ----------------------
  const registerLink = ApiHook.CallRegisterLink(
    registerLinkCheck,
    setRegisterLinkCheck
  );
  if (registerLink.isFetched) {
    window.location.href = registerLink.data?.link;
  }
  const storeLink = ApiHook.CallStoreLink(storeLinkCheck, setStoreLinkCheck);
  if (storeLink.isFetched) {
    window.location.href = storeLink.data?.link;
  }
  return (
    <aside className="left_sidebar">
      <div className="left_mn_toogle_btn" data-tut="expand-side-menu"></div>
      <div
        ref={toggleMenuRef}
        className={`left_navigation_full_hover ${
          isLeftMenuVisible ? "show_mn" : ""
        }`}
      >
        <div
          className="left_mn_toogle_btn"
          onClick={handleLeftMenuToggle}
        ></div>
        <div className="quick_balance_Box_left_logo">
          <img style={{ maxWidth:"200px", maxHeight:"44px"}} src={logo ?? "/images/logo_user.png"} alt="" />
        </div>
        <div className="left_navigation_left_navigation">
          <ul>
            {menuItems?.map((item, index) => (
              <li
                key={index}
                className={`${
                  item.subMenu?.length > 0 ? "drop-menu" : ""
                } ${islinkActive(`/${item.slug}`)}`}
                onClick={() => handleDropMenuClick(index)}
              >
                {item?.subMenu?.length > 0 ? (
                  <>
                    <span className="navigation_ico">
                      <img src={`/images/${item.userIcon}`} alt="" />
                    </span>
                    {t(`${item.slug}`)}
                    {item.subMenu?.length > 0 && (
                      <i className="fa fa-angle-down"></i>
                    )}
                  </>
                ) : item.slug === "events" ? (
                  <Link onClick={handleEventsClick} style={{ cursor: eventsLoading ? 'wait' : 'pointer' }}>
                    <span className="navigation_ico">
                      <img src={`/images/${item.userIcon}`} alt="" />
                    </span>
                    {eventsLoading ? t("loading") || "Loading..." : t(`${item.slug}`)}
                  </Link>
                ) : item.slug === "lms" ? (
                  <Link onClick={handleLmsClick} style={{ cursor: lmsLoading ? 'wait' : 'pointer' }}>
                    <span className="navigation_ico">
                      <img src={`/images/${item.userIcon}`} alt="" />
                    </span>
                    {lmsLoading ? t("loading") || "Loading..." : t(`${item.slug}`)}
                  </Link>
                ) : !item.ecomLink ? (
                  <NavLink to={`/${item.slug}`}>
                    <span className="navigation_ico">
                      <img src={`/images/${item.userIcon}`} alt="" />
                    </span>
                    {t(`${item.slug}`)}
                    {item.subMenu?.length > 0 && (
                      <i className="fa fa-angle-down"></i>
                    )}
                  </NavLink>
                ) : item.slug === "shopping" ? (
                  <Link onClick={handleShoppingClick}>
                    <span className="navigation_ico">
                      <img src={`/images/${item.userIcon}`} alt="" />
                    </span>
                    {t(`${item.slug}`)}
                  </Link>
                ) : (
                  <Link onClick={() => setRegisterLinkCheck(true)}>
                    <span className="navigation_ico">
                      <img src={`/images/${item.userIcon}`} alt="" />
                    </span>
                    {t(`${item.slug}`)}
                  </Link>
                )}
                {item?.subMenu?.length > 0 && (
                  <ul
                    className={`sub-menu ${
                      activeSubMenuIndex === index ? "show_mn" : ""
                    }`}
                  >
                    {item?.subMenu.map((subItem, subIndex) => (
                      <li key={subIndex}>
                        <NavLink to={`/${subItem.slug}`}>
                          {t(`${subItem.slug}`)}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <nav className="left_navigation_section" data-tut="side-navbar" id="tuto2">
        <ul className="left_navigation">
          {menuItems?.map((menuItem, index) =>
            menuItem.slug === "events" && menuItem.isMain ? (
              <li key={index}>
                <OverlayTrigger
                  key={menuItem.slug}
                  trigger={["hover", "focus"]}
                  placement="right"
                  overlay={renderPopover(eventsLoading ? t("loading") || "Loading..." : t(`${menuItem.slug}`))}
                >
                  <Link onClick={handleEventsClick} style={{ cursor: eventsLoading ? 'wait' : 'pointer' }}>
                    <i>
                      <img src={`/images/${menuItem.userIcon}`} alt="" />
                    </i>
                    <span>{menuItem.title}</span>
                  </Link>
                </OverlayTrigger>
              </li>
            ) : menuItem.slug === "lms" && menuItem.isMain ? (
              <li key={index}>
                <OverlayTrigger
                  key={menuItem.slug}
                  trigger={["hover", "focus"]}
                  placement="right"
                  overlay={renderPopover(lmsLoading ? t("loading") || "Loading..." : t(`${menuItem.slug}`))}
                >
                  <Link onClick={handleLmsClick} style={{ cursor: lmsLoading ? 'wait' : 'pointer' }}>
                    <i>
                      <img src={`/images/${menuItem.userIcon}`} alt="" />
                    </i>
                    <span>{menuItem.title}</span>
                  </Link>
                </OverlayTrigger>
              </li>
            ) : menuItem.isMain && !menuItem.ecomLink ? (
              <li key={index}>
                <OverlayTrigger
                  key={menuItem.slug}
                  trigger={["hover", "focus"]}
                  placement="right"
                  
                  overlay={renderPopover(t(`${menuItem.slug}`))}
                >
                  <NavLink
                    className={islinkActive(`/${menuItem.slug}`)}
                    to={menuItem.slug}
                  >
                    <i>
                      <img src={`/images/${menuItem.userIcon}`} alt="" />
                    </i>
                    <span>{menuItem.title}</span>
                  </NavLink>
                </OverlayTrigger>
              </li>
            ) : (
              menuItem.ecomLink &&
              menuItem.isMain && (
                <li key={index}>
                  <OverlayTrigger
                    key={menuItem.slug}
                    trigger={["hover", "focus"]}
                    placement="right"
                    overlay={renderPopover(t(`${menuItem.slug}`))}
                  >
                    <Link onClick={() => setRegisterLinkCheck(true)}>
                      <i>
                        <img src={`/images/${menuItem.userIcon}`} alt="" />
                      </i>
                      <span>{menuItem.title}</span>
                    </Link>
                  </OverlayTrigger>
                </li>
              )
            )
          )}
        </ul>
        {spclMenu &&
          (spclMenu?.ecomLink ? (
            <div
              className="support_menu_btn"
              onClick={handleShoppingClick}
            >
              <img
                src={
                  spclMenu.slug === "shopping"
                    ? "/images/shopping-cart-white_old.svg"
                    : `/images/${spclMenu?.userIcon}`
                }
                alt=""
              />
            </div>
          ) : (
            <div
              className="support_menu_btn"
              onClick={() => {
                if (spclMenu.slug === "shopping" && shoppingRestriction.isRestricted) {
                  toast.warning(shoppingRestriction.message || t("kycRestrictionMessage"));
                  return;
                }
                navigate("/shopping");
              }}
            >
              <img
                src={
                  spclMenu.slug === "shopping"
                    ? "/images/shopping-cart-white_old.svg"
                    : `/images/${spclMenu?.userIcon}`
                }
                alt=""
              />
            </div>
          ))}
      </nav>
    </aside>
  );
};

export default LeftSection;
